import { fmtMoney, fmtNumber } from '../../format';

// Working History (Stephanet's workhistory/shared.jsx): shared formatting, tones and the Excel / print helpers

export const d2 = s => (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
export const d4 = s => (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const my = s => (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '—');
export const period = s => (s ? new Date(`${s}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : '—');
export const naira = v => (v ? fmtMoney(v) : '—');
export const n = v => (v === null || v === undefined || v === '' ? '—' : fmtNumber(v));
export const pct = v => (v === null || v === undefined || v === '' ? '—' : `${Math.round(Number(v))}%`);
export const signed = v => (v === null || v === undefined || v === '' ? '—' : `${v > 0 ? '+' : ''}${fmtNumber(v)}`);
export const initials = name => String(name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

export const STATUS_TONE = {
  Active: 'green', Terminated: 'red', Resigned: 'orange', Suspended: 'yellow', 'On Probation': 'purple', Inactive: 'gray',
  Onboarding: 'blue', Draft: 'gray', Confirmed: 'blue', Transferred: 'indigo', Promoted: 'teal', Changed: 'gray',
};
export const RISK_TONE = { Low: 'green', Medium: 'yellow', High: 'red' };
export const LABEL_TONE = { 'Excellent Performer': 'green', 'Strong Performer': 'blue', Stable: 'teal', Watchlist: 'yellow', 'Critical Risk': 'red' };
export const EVENT_TONE = {
  Hire: 'green', Confirmation: 'teal', 'Probation Started': 'purple', 'Probation Extended': 'purple', Promotion: 'blue', 'Grade Change': 'indigo',
  'Designation Change': 'indigo', Transfer: 'yellow', 'Department Transfer': 'purple', 'Branch Transfer': 'yellow', 'Project Transfer': 'orange',
  'Project Assignment': 'orange', 'Salary Change': 'teal', 'Payroll Adjustment': 'teal', 'Performance Review': 'blue', Award: 'yellow', Commendation: 'yellow',
  Training: 'teal', Certification: 'teal', Warning: 'red', Query: 'orange', Suspension: 'darkred', Incident: 'red', 'Disciplinary Action': 'red',
  'Attendance Concern': 'orange', 'Benefit Added': 'green', 'Benefit Removed': 'gray', 'Asset Issued': 'indigo', 'Asset Returned': 'gray',
  'Tool Issued': 'indigo', 'Tool Returned': 'gray', 'PPE Issued': 'indigo', Resignation: 'orange', Termination: 'darkred', 'End of Contract': 'gray',
  'Exit Clearance': 'gray', 'Rehire Review': 'blue', 'Management Note': 'purple', 'Leave Milestone': 'teal', 'Overtime Approval': 'teal',
};
// green / amber / red badges by value
export const tone = (v, green = [], amber = [], red = []) => (green.includes(v) ? 'green' : amber.includes(v) ? 'yellow' : red.includes(v) ? 'red' : 'gray');

export const TIMELINE_FILTERS = {
  Employment: ['Hire', 'Confirmation', 'Probation Started', 'Probation Extended'],
  Promotions: ['Promotion', 'Grade Change', 'Designation Change'],
  Transfers: ['Transfer', 'Department Transfer', 'Project Transfer', 'Branch Transfer', 'Project Assignment'],
  Performance: ['Performance Review'],
  Incidents: ['Warning', 'Disciplinary Action', 'Query', 'Suspension', 'Incident', 'Attendance Concern'],
  Training: ['Training', 'Certification'],
  Awards: ['Award', 'Commendation'],
  Payroll: ['Salary Change', 'Payroll Adjustment', 'Benefit Added', 'Benefit Removed'],
  Exit: ['Termination', 'Resignation', 'Exit Clearance', 'End of Contract'],
};

const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// one HTML table per section; Excel opens the .xls as a workbook sheet
export function sectionHtml(title, head, rows) {
  return `<h3>${esc(title)}</h3><table border="1"><thead><tr>${head.map(h => `<th>${esc(h)}</th>`).join('')}</tr></thead><tbody>${
    rows.length ? rows.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join('')}</tr>`).join('') : `<tr><td colspan="${head.length}">No records.</td></tr>`
  }</tbody></table><br/>`;
}

export function downloadXls(filename, title, body) {
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"/>
    <style>th{background:#0b318b;color:#fff;font-weight:bold} td,th{padding:4px 8px;font-family:Calibri,Arial;font-size:11pt} h2,h3{font-family:Calibri,Arial}</style>
    </head><body><h2>${esc(title)}</h2>${body}</body></html>`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿', html], { type: 'application/vnd.ms-excel' }));
  a.download = filename;
  a.click();
}

// prints a small document (one event or record) through a hidden frame, leaving the page as it is
export function printDoc(title, rows, footer) {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);
  const body = rows.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');
  frame.contentDocument.write(`<html><head><title>${esc(title)}</title><style>
    body{font-family:Inter,Arial,sans-serif;color:#0d1b24;margin:32px} h1{font-size:18px;margin:0 0 4px;color:#0b318b} p{margin:0 0 18px;color:#51626c;font-size:12px}
    table{border-collapse:collapse;width:100%;font-size:13px} th{width:34%;text-align:left;background:#f1f5fb;color:#334155} th,td{border:1px solid #dbe3f0;padding:8px 10px;vertical-align:top}
    footer{margin-top:28px;font-size:11px;color:#64748b;border-top:1px solid #dbe3f0;padding-top:8px}</style></head>
    <body><h1>${esc(title)}</h1><p>${esc(footer || '')}</p><table>${body}</table><footer>Confidential — Working History · Printed ${new Date().toLocaleString('en-GB')}</footer></body></html>`);
  frame.contentDocument.close();
  setTimeout(() => { frame.contentWindow.focus(); frame.contentWindow.print(); setTimeout(() => frame.remove(), 1000); }, 200);
}
